(function () {
  const LINE_ANIMATION_MS = 1200;
  const LINE_CLICK_MS = 550;

  function getStepIndex(stepNumberEl) {
    const stepId = stepNumberEl.dataset.step;
    if (!stepId) return 1;

    const match = stepId.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 1;
  }

  function isMobileLayout() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function getStepImages(section) {
    return section.querySelectorAll('.steps-image-gallery .steps-image');
  }

  function showStepImage(section, stepIndex) {
    getStepImages(section).forEach((image) => {
      image.classList.toggle('is-visible', image.id === 'js-step-' + stepIndex);
    });
  }

  function getStepLineTarget(row, stepIndex) {
    const steps = row.querySelectorAll('.step-item');
    const activeStep = steps[stepIndex - 1];
    if (!activeStep) return null;

    const rowRect = row.getBoundingClientRect();
    const circle = activeStep.querySelector('.step-number');
    const circleRect = circle.getBoundingClientRect();

    if (isMobileLayout()) {
      const firstCircle = steps[0].querySelector('.step-number').getBoundingClientRect();
      const lastCircle = steps[steps.length - 1].querySelector('.step-number').getBoundingClientRect();
      const trackTop = firstCircle.top + firstCircle.height / 2;
      const trackBottom = lastCircle.top + lastCircle.height / 2;
      const trackHeight = trackBottom - trackTop;

      if (trackHeight <= 0) return null;

      const endY = circleRect.top + circleRect.height / 2;
      const scale = Math.min(1, Math.max(0, (endY - trackTop) / trackHeight));

      return { type: 'scale', value: scale };
    }

    const endX = circleRect.left + circleRect.width / 2 - rowRect.left;

    return { type: 'width', value: Math.max(0, endX) };
  }

  function updateProgressLine(row, stepIndex) {
    const target = getStepLineTarget(row, stepIndex);
    if (!target) return;

    if (target.type === 'scale') {
      row.style.setProperty('--progress-scale', String(target.value));
      return;
    }

    row.style.setProperty('--progress-width', target.value + 'px');
  }

  function setActiveSteps(row, stepIndex) {
    row.dataset.activeStep = String(stepIndex);

    row.querySelectorAll('.step-item').forEach((step, index) => {
      step.classList.toggle('is-active', index + 1 <= stepIndex);
    });
  }

  function getFillScale(fill) {
    const transform = window.getComputedStyle(fill).transform;

    if (!transform || transform === 'none') return 0;

    const matrix = transform.match(/matrix\(([^)]+)\)/);
    if (matrix) {
      const values = matrix[1].split(',').map(parseFloat);
      return values.length === 6 ? values[3] : 0;
    }

    const scaleMatch = transform.match(/scaleY\(([^)]+)\)/);
    return scaleMatch ? parseFloat(scaleMatch[1]) : 0;
  }

  function animateLineToStep(row, stepIndex, section, isScrollAnimation) {
    const fill = row.querySelector('.steps-progress-fill');
    const target = getStepLineTarget(row, stepIndex);
    if (!fill || !target) return Promise.resolve();

    const property = target.type === 'scale' ? 'transform' : 'width';
    const duration = isScrollAnimation ? LINE_ANIMATION_MS : LINE_CLICK_MS;
    let activated = false;

    const activateStepNow = () => {
      if (activated) return;
      activated = true;
      setActiveSteps(row, stepIndex);
      showStepImage(section, stepIndex);
    };

    updateProgressLine(row, stepIndex);

    return new Promise((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        fill.removeEventListener('transitionend', onEnd);
        activateStepNow();
        resolve();
      };

      const onEnd = (event) => {
        if (event.target === fill && event.propertyName === property) {
          finish();
        }
      };

      const watchReach = () => {
        if (activated || settled) return;

        if (target.type === 'width') {
          if (fill.getBoundingClientRect().width >= target.value - 1) {
            activateStepNow();
          }
        } else if (getFillScale(fill) >= target.value - 0.02) {
          activateStepNow();
        }

        if (!settled) requestAnimationFrame(watchReach);
      };

      fill.addEventListener('transitionend', onEnd);
      requestAnimationFrame(watchReach);
      setTimeout(finish, duration + 50);
    });
  }

  async function runScrollAnimation(section, row) {
    if (row.dataset.scrollAnimated === 'true') return;

    row.dataset.scrollAnimated = 'true';
    row.classList.add('is-animating');

    const totalSteps = row.querySelectorAll('.step-item').length;

    setActiveSteps(row, 1);
    showStepImage(section, 1);
    updateProgressLine(row, 1);

    for (let step = 2; step <= totalSteps; step++) {
      await animateLineToStep(row, step, section, true);
    }

    row.classList.remove('is-animating');
    row.classList.add('animation-complete');
  }

  function initStepsSection(section) {
    const row = section.querySelector('.steps-row');
    if (!row || row.dataset.stepsInitialized === 'true') return;

    row.dataset.stepsInitialized = 'true';
    setActiveSteps(row, 1);
    showStepImage(section, 1);
    updateProgressLine(row, 1);

    row.querySelectorAll('.step-number').forEach((stepNumber) => {
      stepNumber.addEventListener('click', function () {
        const stepIndex = getStepIndex(this);
        setActiveSteps(row, stepIndex);
        updateProgressLine(row, stepIndex);
        showStepImage(section, stepIndex);
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            runScrollAnimation(section, row);
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.3, rootMargin: '0px 0px -10% 0px' }
      );

      observer.observe(section);
    } else {
      runScrollAnimation(section, row);
    }

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        const activeStep = parseInt(row.dataset.activeStep, 10) || 1;
        updateProgressLine(row, activeStep);
      });

      resizeObserver.observe(row);
    }
  }

  function initAllStepsSections(root) {
    const scope = root || document;
    scope.querySelectorAll('.custom-steps-section').forEach(initStepsSection);
  }

  document.addEventListener('DOMContentLoaded', () => initAllStepsSections());
  document.addEventListener('shopify:section:load', (event) => initAllStepsSections(event.target));
})();
